import { useState } from 'react';
import { healthCardIssue, type ApiClient, type HealthCard } from '@aithora/shared';
import { sx } from '../../ui/designStyles';

// The SMART Health Card (health_card_view.dart), restored from the Flutter production console
// that the design does not draw. It sits under the Life Story card in the design's card style,
// and reads or writes only served routes. The resident's devices are the V8 Devices card on
// the Overview (chartExtraVals.ts).

const CARD = 'border-top:1px solid rgba(34,39,46,.14);padding:13px 15px;min-width:0;';
const HEAD = 'font-size:16px;color:#3C454F;line-height:1.5;';
const LINK = 'display:inline-flex;align-items:center;min-height:28px;padding:0;font-size:16px;font-weight:600;color:#2F5C97;background:transparent;border:0;cursor:pointer;font-family:inherit;text-decoration:none;';
const NOTE = 'font-size:14px;color:#54606C;line-height:1.5;';

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The SMART Health Card: issue, copy, and the offline verifier. */
export function HealthCardCard({ client, residentId }: { client: ApiClient; residentId: string }) {
  const [card, setCard] = useState<HealthCard | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const issue = async () => {
    setBusy(true); setMsg(null);
    try { setCard(await healthCardIssue(client, { params: { userId: residentId } })); } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!card) return;
    const text = JSON.stringify({ verifiableCredential: card.verifiableCredential });
    try { await navigator.clipboard.writeText(text); setMsg('Card copied. Paste it into any SMART Health Card verifier.'); } catch { setMsg('Copy did not work in this browser.'); }
  };
  // The issuer serves the offline verifier page (server.ts GET /health-card-verifier).
  const verifier = (card?.issuer ?? '').replace(/\/+$/, '') + '/health-card-verifier';
  return (
    <div data-testid="chart-health-card" style={sx(CARD)}>
      <div style={sx(HEAD)}><span style={sx('font-weight:700;')}>Health card</span>: a signed SMART Health Card any facility can check offline, without contacting Aithora.</div>
      <div style={sx('display:flex;align-items:center;gap:14px;flex-wrap:wrap;row-gap:4px;margin-top:9px;')}>
        <button type="button" disabled={busy} onClick={() => void issue()} style={sx(LINK)}>{card ? 'Issue again' : 'Issue card'}</button>
        {card && <button type="button" onClick={() => void copy()} style={sx(LINK)}>Copy card</button>}
        {card && <a href={verifier} target="_blank" rel="noopener noreferrer" style={sx(LINK)}>Open the offline verifier</a>}
      </div>
      {card && (
        <div style={sx(NOTE + 'margin-top:6px;')}>
          {'Signed by key ' + card.kid + ' · card ' + card.rid.slice(0, 8) + (card.ephemeralKey ? ' · test key: it will not verify after a restart' : '') + '. Authenticity is cryptographic; confirm the holder separately.'}
        </div>
      )}
      {msg && <div role="status" style={sx(NOTE + 'color:#2F5C97;margin-top:6px;')}>{msg}</div>}
    </div>
  );
}
